if (!customElements.get('collection-switcher')) {
  class CollectionSwitcher extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.buttons = this.querySelectorAll('.collection-link');
      this.productsContainer = this.querySelector('.collection-switcher__products');
      this.viewAllLink = this.querySelector('.collection-switcher__view-all');
      this.fetchAbortController = null;

      if (!this.sectionId || !this.productsContainer) {
        console.warn('CollectionSwitcher: Missing required data attributes or elements');
        return;
      }

      this.addListeners();

      // Set first button as active by default
      if (this.buttons.length > 0) {
        this.buttons[0].classList.add('is-active');
      }
    }

    addListeners() {
      this.buttons.forEach((button) => {
        button.addEventListener('click', this.handleButtonClick.bind(this));
      });
    }

    async handleButtonClick(evt) {
      const button = evt.currentTarget;
      const collectionHandle = button.dataset.collectionHandle;

      if (!collectionHandle) {
        console.warn('CollectionSwitcher: Button missing data-collection-handle attribute');
        return;
      }

      // Don't refetch if already active
      if (button.classList.contains('is-active')) {
        return;
      }

      await this.switchCollection(collectionHandle, button);
    }

    async switchCollection(collectionHandle, activeButton) {
      try {
        // Set loading state
        this.setLoadingState(true);
        this.updateActiveButton(activeButton);

        // Cancel any in-flight requests
        if (this.fetchAbortController) {
          this.fetchAbortController.abort('Collection changed');
        }
        this.fetchAbortController = new AbortController();

        // Fetch section with new collection context
        const fetchUrl = `/collections/${collectionHandle}?section_id=${this.sectionId}`;
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.fetchAbortController.signal
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const html = await response.text();

        // Parse the response HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract the products container from the response
        const newProductsContainer = doc.querySelector('.collection-switcher__products');

        if (newProductsContainer) {
          // Replace the products container content
          this.productsContainer.innerHTML = newProductsContainer.innerHTML;

          // Update "Shop all" link if present
          if (this.viewAllLink) {
            const newViewAllLink = doc.querySelector('.collection-switcher__view-all');
            if (newViewAllLink) {
              this.viewAllLink.href = newViewAllLink.href;
            }
          }

          // Reinitialize any components that might be in the new content
          this.reinitializeComponents();

          // Broadcast update event for any listeners
          this.dispatchEvent(new CustomEvent('collection-switched', {
            bubbles: true,
            detail: { collectionHandle }
          }));
        } else {
          console.warn('CollectionSwitcher: Could not find products container in response');
        }

      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('CollectionSwitcher: Error switching collection', error);
        }
      } finally {
        this.setLoadingState(false);
      }
    }

    setLoadingState(isLoading) {
      if (isLoading) {
        this.productsContainer.classList.add('is-loading');
        this.buttons.forEach((btn) => {
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
        });
      } else {
        this.productsContainer.classList.remove('is-loading');
        this.buttons.forEach((btn) => {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
        });
      }
    }

    updateActiveButton(activeButton) {
      this.buttons.forEach((btn) => {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-pressed', 'false');
      });

      if (activeButton) {
        activeButton.classList.add('is-active');
        activeButton.setAttribute('aria-pressed', 'true');
      }
    }

    reinitializeComponents() {
      // Reinitialize carousel if present
      const carousel = this.productsContainer.querySelector('carousel-slider');
      if (carousel && carousel.reload) {
        carousel.reload();
      }

      // Reinitialize quick add if enabled
      const quickAddButtons = this.productsContainer.querySelectorAll('quick-add');
      quickAddButtons.forEach((quickAdd) => {
        if (quickAdd.connectedCallback) {
          quickAdd.connectedCallback();
        }
      });
    }
  }

  customElements.define('collection-switcher', CollectionSwitcher);
}
